import * as React from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { isEmpty } from "../../utils/Util";
import { LocaleHelper } from "../../helpers/LocaleHelper";

let dollarUSLocale = LocaleHelper.getCurrencyFormatter("US");

interface IPaymentScreenHeaderProps {
  invoiceId: string | null;
  requestedAmount: number | null;
  platformFee: number | null;
  totalAmount: number | null;
  platformFeeFlag: boolean;
  amountLabel: string;
  status: string | null;
  customerName: string | null;
  customerId: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  isProcessing: boolean;
  callbackPosted: boolean;
  isSaveCardFlow?: boolean;
  processingMessage?: string;
}

export function PaymentScreenHeader({
  invoiceId,
  requestedAmount,
  platformFee,
  totalAmount,
  platformFeeFlag,
  amountLabel,
  status,
  customerName,
  customerId,
  customerEmail,
  customerPhone,
  isProcessing,
  callbackPosted,
  isSaveCardFlow = false,
  processingMessage = "Do not close or refresh this page. Closing before completion may result in payment cancellations/reversals.",
}: IPaymentScreenHeaderProps) {
  const normalizedStatus =
    status && status.toLowerCase() === "authorised" ? "Authorized" : status;

  const statusChipColor = (() => {
    if (!normalizedStatus) return "default";
    const s = normalizedStatus.toLowerCase();
    if (["success", "authorized", "card saved", "refunded", "partial refunded"].some(v => s.includes(v)))
      return "success";
    if (["failed", "failure", "chargeback", "refund failed"].some(v => s.includes(v)))
      return "error";
    return "warning";
  })() as "success" | "error" | "warning" | "default";

  const customerFields = [customerName, customerId, customerEmail, customerPhone].filter(
    (v) => !isEmpty(v)
  );

  return (
    <Box
      aria-label="Order and customer context"
      sx={{
        display: "flex",
        alignItems: "center",
        px: 3,
        py: 1.5,
        borderBottom: "1px solid #E5E7E9",
        bgcolor: "#F8F9F9",
        gap: 3,
      }}
    >
      {/* Left — amount (hidden on save-card flows) */}
      {!isSaveCardFlow && (
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.65rem",
              lineHeight: 1.6,
            }}
          >
            {amountLabel}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, lineHeight: 1.15, letterSpacing: -0.5 }}
          >
            {dollarUSLocale.format(requestedAmount ?? 0)}
          </Typography>
          {platformFeeFlag && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Platform fee: {dollarUSLocale.format(platformFee ?? 0)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", fontWeight: 600 }}
              >
                Total: {dollarUSLocale.format(totalAmount ?? 0)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Right — customer + order info, light blue background */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: "right",
        }}
      >
        {callbackPosted && !isEmpty(status) ? (
          <Box sx={{ mb: 0.5, display: "flex", justifyContent: "flex-end" }}>
            <Chip
              label={normalizedStatus}
              size="medium"
              color={statusChipColor}
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: "0.9rem", height: 28, px: 0.5 }}
            />
          </Box>
        ) : isProcessing ? (
          <Box sx={{ mb: 0.5, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75 }}>
            <CircularProgress size={18} thickness={5} sx={{ color: "#D32F2F" }} />
            <Typography sx={{ color: "#D32F2F", fontWeight: 900, fontSize: "1rem" }}>
              {processingMessage}
            </Typography>
          </Box>
        ) : null}
        {!isSaveCardFlow && !isEmpty(invoiceId) && (
          <Typography
            variant="body2"
            sx={{ color: "text.primary", fontWeight: 600, letterSpacing: 0.1 }}
          >
            Order #{invoiceId}
          </Typography>
        )}
        {customerFields.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              wordBreak: "break-all",
              display: "block",
              fontSize: "0.8rem",
              mt: 0.25,
            }}
          >
            {customerFields.join("  ·  ")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
